import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as viewCurrentStockAPI from '../../api/Stock/currentStockAPI';

// View Current Stock — same endpoints as legacy /Reports/ItemStockReport.

const errMsg = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || (typeof err === 'string' ? err : fallback);

const thunk = (type, fn, fallback) => createAsyncThunk(type, async (params, { rejectWithValue }) => {
    try { return await fn(params); }
    catch (err) { return rejectWithValue(errMsg(err, fallback)); }
});

export const fetchStockCostCenterCodes = thunk('viewcurrentstock/fetchCCs', viewCurrentStockAPI.getStockCostCenterCodes, 'Failed to load cost centers');
export const fetchStockCategories      = thunk('viewcurrentstock/fetchCategories', viewCurrentStockAPI.getStockCategories, 'Failed to load categories');
export const fetchItemCodeSuggestions  = thunk('viewcurrentstock/fetchItemCodes', viewCurrentStockAPI.getItemCodeAutoComplete, 'Failed to load item codes');
export const fetchViewItemsGrid        = thunk('viewcurrentstock/fetchGrid', viewCurrentStockAPI.getViewItemsGrid, 'Failed to load stock');

const initialState = {
    costCenters: [],      // [{ CCID, CCVAL }]
    categories: [],       // [{ Categoryid, CategoryValue }]
    itemSuggestions: [],  // [{ SearchItem }]
    stockRows: [],        // [{ Costcenter, Itemcode, ItemName, Specification, DCA, SubDca, Basic, Units, Qty, TotalValue }]
    stockLoaded: false,
    loading: { costCenters: false, categories: false, itemSuggestions: false, stockRows: false },
    errors:  { costCenters: null,  categories: null,  itemSuggestions: null,  stockRows: null },
};

const viewCurrentStockSlice = createSlice({
    name: 'viewcurrentstock',
    initialState,
    reducers: {
        clearStockRows: (s) => { s.stockRows = []; s.stockLoaded = false; s.errors.stockRows = null; },
        clearItemSuggestions: (s) => { s.itemSuggestions = []; },
        resetViewCurrentStock: () => initialState,
    },
    extraReducers: (builder) => {
        const list = (t, key, after) => builder
            .addCase(t.pending,   (s) => { s.loading[key] = true; s.errors[key] = null; })
            .addCase(t.fulfilled, (s, a) => { s.loading[key] = false; s[key] = a.payload?.Data || []; if (after) after(s); })
            .addCase(t.rejected,  (s, a) => { s.loading[key] = false; s[key] = []; s.errors[key] = a.payload; });

        list(fetchStockCostCenterCodes, 'costCenters');
        list(fetchStockCategories, 'categories');
        list(fetchItemCodeSuggestions, 'itemSuggestions');
        list(fetchViewItemsGrid, 'stockRows', (s) => { s.stockLoaded = true; });
    },
});

export const { clearStockRows, clearItemSuggestions, resetViewCurrentStock } = viewCurrentStockSlice.actions;

const st = (s) => s.viewcurrentstock;
export const selectStockCostCenters  = (s) => st(s).costCenters;
export const selectStockCategories   = (s) => st(s).categories;
export const selectItemSuggestions   = (s) => st(s).itemSuggestions;
export const selectStockRows         = (s) => st(s).stockRows;
export const selectStockLoaded       = (s) => st(s).stockLoaded;
export const selectStockLoading      = (s) => st(s).loading;
export const selectStockErrors       = (s) => st(s).errors;

export default viewCurrentStockSlice.reducer;
